import { useMemo } from "react";

import { useToast } from "@/ds";
import {
  createExportFeedback,
  printGanttView,
  recordPdfExportAudit,
  useFileExport,
} from "@/modules/export";
import type {
  ExportFormat,
  PdfExportAuditResource,
  ToastLike,
  UseFileExportOptions,
} from "@/modules/export";

interface CargaExportacaoOptions {
  fileName: string;
  /** Sem dados para exportar: o Exportar fica desabilitado e o arquivo não é pedido. */
  isEmpty: boolean;
  loadFile: UseFileExportOptions["loadFile"];
  /** Dados da auditoria do PDF (RN007): a impressão do navegador não passa pelo servidor. */
  auditoria: {
    recurso: PdfExportAuditResource;
    quantidade: number;
    inicio: string;
    fim: string;
    busca: string;
  };
  printTitle: string;
}

/**
 * Exportar Visualização da Carga: Excel/CSV pelo serviço e PDF pela impressão do navegador (com a auditoria avisada daqui).
 * O retorno ao usuário usa o `useToast` do DS.
 */
export function useCargaExportacao({
  fileName,
  isEmpty,
  loadFile,
  auditoria,
  printTitle,
}: CargaExportacaoOptions) {
  const toast = useToast();
  const feedback = useMemo(() => {
    const toastRef: { current: ToastLike } = {
      current: {
        show: ({ severity, summary, detail, life }) =>
          toast.show({ severity, summary, detail, life }),
      },
    };
    return createExportFeedback(toastRef);
  }, [toast]);

  const exportView = useFileExport({ fileName, loadFile, isEmpty: () => isEmpty, ...feedback });

  function exportar(format: ExportFormat) {
    if (format !== "pdf") {
      void exportView.run(format);
      return;
    }
    exportView.close();
    void recordPdfExportAudit({
      recurso: auditoria.recurso,
      quantidade: auditoria.quantidade,
      dataInicio: auditoria.inicio,
      dataFim: auditoria.fim,
      busca: auditoria.busca,
    }).then(() => printGanttView(printTitle));
  }

  return { exportView, exportar };
}
